import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Col, Row } from '@zendeskgarden/react-grid';
import { SM, LG } from '@zendeskgarden/react-typography';
import { omit, noop, startCase, replace, map, sortBy, size } from 'lodash';
import {
  Dropdown,
  Select,
  Item,
  Menu,
  Field,
} from '@zendeskgarden/react-dropdowns';
import Flex from 'styled-flex-component';

import { HelmetWrapper } from 'utility/DefaultMeta';
import BreadCrumbs from 'components/BreadCrumbs/BreadCrumbs';
import { PublicItemSelectionFilters } from 'components/ItemSelectionFilters/ItemSelectionFilters';
import PublicProductsTable from 'components/PublicProductsTable/PublicProductsTable';
import formatSortFields from 'utility/formatSortFields';
import { variables } from 'theme/variables';
import LinkWithParams from 'components/LinkWithParams/LinkWithParams';
import {
  facetsShape,
  listingShape,
  locationShape,
  metallurgyShape,
  paginationShape,
} from 'propTypesObjects';
import CategoryFilterLinks from './components/CategoryFilterLinks/CategoryFilterLinks';
import CategoryAlloyHero from './components/CategoryAlloyHero/CategoryAlloyHero';
import {
  CategoriesSection,
  CategoriesSectionInner,
  CategoriesWrapper,
  CategoriesWrapperInner,
  Content,
  DescriptionCard,
  Filters,
  CategoryItemRedesign,
} from './CategoryDetailsRedesign.styles';
import {
  HeroCard,
  HeroDescription,
  HeroTitle,
} from './components/CategoryAlloyHero/CategoryAlloyHero.styles';

const { public_app_max_width: publicAppMaxWidth } = variables;

function RawCategories({ alloys }) {
  const groups = alloys?.groups?.results;
  return (
    <div>
      <HeroCard
        style={{
          marginBottom: '20px',
        }}
      >
        <Grid>
          <Row justifyContent="between" alignItems="center">
            <Col md={6} lg="auto">
              <Flex
                column
                justifyCenter
                style={{
                  marginTop: '30px',
                  height: '100%',
                }}
              >
                <HeroTitle>Alloy Guide</HeroTitle>
                <HeroDescription>
                  Select an alloy or alloy group below to view technical details
                  and matching products.
                </HeroDescription>
              </Flex>
            </Col>
          </Row>
        </Grid>
      </HeroCard>
      {groups && size(groups) && (
        <div>
          <LG slate>Groups</LG>
          <Row style={{ marginBottom: '40px' }}>
            {map(sortBy(groups, 'name'), (group) => {
              const link = `/c/octg/${group.slug}`;
              return (
                <Col
                  style={{
                    minWidth: '140px',
                    maxWidth: '140px',
                    marginTop: '10px',
                  }}
                  key={group.id}
                >
                  <CategoryItemRedesign>
                    <LinkWithParams to={link}>{group.name}</LinkWithParams>
                  </CategoryItemRedesign>
                </Col>
              );
            })}
          </Row>
        </div>
      )}
      <LG slate>Alloys</LG>
      <Row style={{ marginBottom: '40px' }}>
        {map(
          sortBy(alloys?.alloys?.results, 'metallurgy_group_name'),
          (item, i, arr) => {
            const link = `/c/octg/${item.path || item.slug}`;
            const last = arr[i - 1];
            const isDiffGroup =
              (i === 0 && item.metallurgy_group_name) ||
              (last &&
                last.metallurgy_group_name !== item.metallurgy_group_name);
            return (
              <React.Fragment key={`${i}-frag`}>
                {isDiffGroup ? (
                  <Col
                    style={{ marginTop: i === 0 ? '0px' : '25px' }}
                    key={`${i}-name`}
                    size="12"
                  >
                    <SM bold uppercase paddingBottomSm>
                      {item.metallurgy_group_name}
                    </SM>
                  </Col>
                ) : null}
                <Col
                  style={{
                    minWidth: '140px',
                    maxWidth: '140px',
                    marginTop: '10px',
                  }}
                  key={`${i}-value`}
                >
                  <CategoryItemRedesign>
                    <LinkWithParams to={link}>{item.name}</LinkWithParams>
                  </CategoryItemRedesign>
                </Col>
              </React.Fragment>
            );
          }
        )}
      </Row>
    </div>
  );
}

RawCategories.propTypes = {
  alloys: PropTypes.shape({
    alloys: PropTypes.shape({
      results: PropTypes.arrayOf(
        PropTypes.shape({
          metallurgy_group_name: PropTypes.string,
        })
      ),
    }),
    groups: PropTypes.shape({
      results: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
        })
      ),
    }),
  }),
};

function RenderDropdown({ loading, sortFields, pagination, fetchMore }) {
  const [localSort, setLocalSort] = useState();
  const sort =
    pagination?.active?.sort?.sort && !loading
      ? pagination?.active?.sort
      : localSort || {};

  function onClickSort({ field }) {
    const data = formatSortFields({ field, sort });
    setLocalSort(data.sorting);
    fetchMore({
      ...data,
      filters: pagination?.active?.filters,
      pagination: pagination?.active?.pagination,
    });
  }

  return (
    <Dropdown selectedItem={sort.sort} onSelect={noop}>
      <Field>
        <Select
          style={{ minWidth: '100%', maxWidth: '100%' }}
          background="#fff"
          active={false}
          fontSize="13px"
        >
          {sort.sort ? (
            <SM>
              <SM as="span" bold>
                Sorting:{' '}
              </SM>{' '}
              <SM as="span">{startCase(replace(sort.sort, /_/g, ' '))}</SM>{' '}
              <SM as="span" grey>
                ({sort.direction})
              </SM>
            </SM>
          ) : (
            <SM slate bold>
              Sort Products
            </SM>
          )}
        </Select>
      </Field>
      <Menu maxHeight="200px">
        {map(
          sortBy(
            map(sortFields, (o) => ({ option: o })),
            'option'
          ),
          ({ option }) => (
            <Item
              onClick={() => {
                onClickSort({ field: option });
              }}
              key={option}
              value={option}
            >
              {startCase(replace(option, /_/g, ' '))}
            </Item>
          )
        )}
      </Menu>
    </Dropdown>
  );
}

RenderDropdown.propTypes = {
  loading: PropTypes.bool,
  sortFields: PropTypes.arrayOf(PropTypes.string),
  pagination: paginationShape,
  fetchMore: PropTypes.func,
};

function CategoryDetailsRedesign({
  metadata,
  alloyGroupDetails,
  alloyDetails,
  groupDescription,
  alloyDescription,
  catType,
  alloyGroupSlug,
  alloySlug,
  keyword,
  noSlug,
  showAdvancedFilters,
  setShowAdvancedFilters,
  productsAreLoading,
  categories,
  paginationAndFilterPaths,
  fetchCategories,
  onChangeCriteria,
  rfqItemFilters,
  location,
  alloyList,
  groupId,
  otherAlloys,
  alloyId,
}) {
  const categoriesResult = categories?.result;

  function renderBreadcrumbs() {
    if (catType) {
      return (
        <BreadCrumbs
          list={[
            { title: catType, link: `/c/${catType}` },
            {
              title: alloyGroupSlug,
              link: `/c/${catType}/${alloyGroupSlug}`,
            },
            {
              title: alloySlug,
              link: `/c/${catType}/${alloyGroupSlug}/${alloySlug}`,
            },
          ]}
        />
      );
    }
    if (keyword) {
      return <BreadCrumbs list={[{ title: `Search Results: ${keyword}` }]} />;
    }
    return <BreadCrumbs list={[{ title: `Categories` }]} />;
  }

  function renderDescription() {
    if (alloyDescription) {
      return (
        <>
          <LG slate>Alloy Description</LG>
          <DescriptionCard>{alloyDescription}</DescriptionCard>
        </>
      );
    }
    if (groupDescription) {
      return (
        <>
          <LG slate>Group Description</LG>
          <DescriptionCard>{groupDescription}</DescriptionCard>
        </>
      );
    }
    return null;
  }

  return (
    <CategoriesWrapper
      style={{
        width: '100%',
        maxWidth: publicAppMaxWidth,
        margin: '0 auto',
      }}
    >
      <HelmetWrapper metadata={metadata} />
      <div style={{ padding: '0 20px', marginBottom: '10px' }}>
        <Grid>
          <Row>
            <Col>{renderBreadcrumbs()}</Col>
          </Row>
        </Grid>
      </div>
      <CategoriesWrapperInner>
        <Filters
          style={{
            maxWidth: '230px',
            minWidth: '230px',
            padding: '0 40px 0 0',
          }}
        >
          {alloyList?.alloys ? (
            <CategoryFilterLinks location={location} alloys={alloyList} />
          ) : (
            <div />
          )}
        </Filters>
        <Content>
          {noSlug ? <RawCategories alloys={alloyList} /> : null}

          {alloyDetails || alloyGroupDetails ? (
            <CategoriesSection>
              <CategoriesSectionInner>
                <CategoryAlloyHero
                  alloyGroupSlug={alloyGroupSlug}
                  alloySlug={alloySlug}
                  alloyGroupDetails={alloyGroupDetails}
                  alloyDetails={alloyDetails}
                  categories={categories}
                />
              </CategoriesSectionInner>
            </CategoriesSection>
          ) : null}

          {renderDescription()}
          {!!groupId && !alloySlug && (
            <div style={{ marginTop: '20px' }}>
              <LG slate>Alloys</LG>
              <Row>
                {map(otherAlloys, (alloy, i) => (
                  <Col
                    className="alloyColumns"
                    style={{ maxWidth: '100px' }}
                    alignCenter
                    justifyCenter
                    key={`de-${i}`}
                  >
                    <CategoryItemRedesign active={alloy.id === Number(alloyId)}>
                      <LinkWithParams to={`/c/octg/${alloy.path}`}>
                        {alloy.name}
                      </LinkWithParams>
                    </CategoryItemRedesign>
                  </Col>
                ))}
              </Row>
            </div>
          )}
          {!noSlug ? (
            <CategoriesSection>
              <CategoriesSectionInner>
                <div style={{ margin: '40px 0' }}>
                  <Row
                    alignCenter
                    justifyBetween
                    style={{ marginBottom: '20px' }}
                  >
                    <Col size="auto" alignCenter>
                      <LG slate>Alloy Products</LG>
                    </Col>
                    <Col justifyEnd alignCenter>
                      <Row>
                        <Col>
                          <SM
                            style={{
                              whiteSpace: 'nowrap',
                              padding: '12px 0',
                            }}
                            onClick={() =>
                              setShowAdvancedFilters(!showAdvancedFilters)
                            }
                            link
                          >
                            {showAdvancedFilters
                              ? 'Hide Filters'
                              : 'Product Search'}
                          </SM>
                        </Col>
                        <Col>
                          <div style={{ minWidth: '260px' }}>
                            <RenderDropdown
                              loading={productsAreLoading}
                              {...{
                                sortFields: categoriesResult?.sort_fields,
                                pagination: paginationAndFilterPaths,
                                fetchMore: fetchCategories,
                              }}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  {showAdvancedFilters ? (
                    <Row>
                      <Col size="12">
                        <PublicItemSelectionFilters
                          inline
                          rowIndex={0}
                          onChangeFilters={onChangeCriteria}
                          filters={omit(rfqItemFilters, [
                            'category',
                            'quantity',
                          ])}
                        />
                      </Col>
                    </Row>
                  ) : null}
                  <PublicProductsTable
                    loading={productsAreLoading}
                    items={categoriesResult?.docs}
                    fetchMore={({ pagination, filters, sorting }) => {
                      fetchCategories({
                        pagination,
                        criteria: filters,
                        sorting,
                      });
                    }}
                    pagination={paginationAndFilterPaths}
                  />
                </div>
              </CategoriesSectionInner>
            </CategoriesSection>
          ) : null}
        </Content>
      </CategoriesWrapperInner>
    </CategoriesWrapper>
  );
}

CategoryDetailsRedesign.propTypes = {
  metadata: PropTypes.shape({}),
  alloyGroupDetails: PropTypes.shape({}),
  alloyDetails: PropTypes.shape({}),
  groupDescription: PropTypes.string,
  alloyDescription: PropTypes.string,
  catType: PropTypes.string,
  alloyGroupSlug: PropTypes.string,
  alloySlug: PropTypes.string,
  keyword: PropTypes.string,
  noSlug: PropTypes.bool,
  showAdvancedFilters: PropTypes.bool,
  setShowAdvancedFilters: PropTypes.func,
  productsAreLoading: PropTypes.bool,
  categories: PropTypes.shape({
    result: PropTypes.shape({
      docs: PropTypes.arrayOf(listingShape),
    }),
    sort_fields: PropTypes.arrayOf(PropTypes.string),
  }),
  paginationAndFilterPaths: facetsShape,
  fetchCategories: PropTypes.func,
  onChangeCriteria: PropTypes.func,
  rfqItemFilters: PropTypes.shape({}),
  location: locationShape,
  alloyList: PropTypes.shape({
    groups: PropTypes.shape({}),
    alloys: PropTypes.shape({}),
  }),
  groupId: PropTypes.number,
  otherAlloys: PropTypes.arrayOf(metallurgyShape),
  alloyId: PropTypes.number,
};

export default CategoryDetailsRedesign;
